import { useState, useEffect } from 'react';
import { api } from '@/App';
import Navbar from '@/components/Navbar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function TermsPage({ user, onLogout }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTerms();
  }, []);

  const loadTerms = async () => {
    try {
      const response = await api.get('/legal/terms');
      const defaultContent = `# Terms and Conditions

## 1. Acceptance of Terms

By registering and using NLV (NL Volleyball) services, you agree to be bound by these Terms and Conditions.

## 2. Membership and Registration

- All members must provide accurate and complete information during registration
- Members are responsible for maintaining the confidentiality of their account
- Members must be at least 18 years old or have parental consent

## 3. Game Participation

- Members must register for games through the official platform
- Cancellations must be made at least 24 hours before the game
- Payment is required for all confirmed registrations

## 4. Code of Conduct

Members agree to:

- **Respect** all players, staff, and facilities
- **Follow** the rules and regulations of the sport
- **Maintain** appropriate behavior at all times
- **Report** any issues or concerns to administrators

## 5. Liability

- NLV is not responsible for injuries during games
- Members participate at their own risk
- Proper insurance coverage is recommended

## 6. Payment and Fees

- All fees must be paid before or at the time of the game
- Refunds are subject to club policy
- Unpaid fees may result in suspension of membership

## 7. Changes to Terms

NLV reserves the right to modify these terms at any time. Members will be notified of significant changes.

## 8. Contact

For questions about these terms, please contact us through the contact form on our website.

---

*Last updated: ${new Date().toLocaleDateString()}*`;
      
      setContent(response.data.content || defaultContent);
    } catch (error) {
      console.error('Failed to load terms:', error);
      setContent('# Terms and Conditions\n\nFailed to load terms and conditions.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50">
      {user && <Navbar user={user} onLogout={onLogout} />}
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading...</p>
            </div>
          ) : (
            <div className="legal-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({node, ...props}) => <h1 className="text-4xl font-bold text-gray-900 mb-6" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 text-gray-700" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 text-gray-700" {...props} />,
                  li: ({node, ...props}) => <li className="mb-2" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-bold text-orange-600" {...props} />,
                  hr: ({node, ...props}) => <hr className="my-8 border-gray-300" {...props} />,
                  a: ({node, ...props}) => <a className="text-orange-600 underline hover:text-orange-700" {...props} />
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
